import React from 'react';
import styles from './ServiceInsights.module.scss';
import { PriceFormat, Spinner } from 'components/UI';
import type { ServicesInsightsReport } from 'model/ServicesInsightsReport';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

interface Props {
    loading: boolean;
    report: ServicesInsightsReport;
}

const ServicesTable: React.FC<Props> = ({ loading, report }) => {
    const rows = report?.services ?? [];
    const totals = report.totals;

    return (
        <div className={styles.tableWrapper}>
            <div className={styles.tableHeaderRow}>
                <div>Service Name</div>
                <div>Category</div>
                <div className={styles.right}>Quantity</div>
                <div className={styles.right}>Avg Price</div>
                <div className={styles.right}>Gross Sales</div>
                <div className={styles.right}>Discounts</div>
                <div className={styles.refundColumn}>
                    Refunds
                    <Tooltip
                        content="Refunds are proportionally allocated from order-level refunds"
                        placement="top"
                        className={styles.customTooltip}
                    >
                        <div>
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className={styles.tooltipIcon}
                            />
                        </div>
                    </Tooltip>
                </div>
                <div className={styles.right}>Net Sales</div>
                <div className={styles.right}>% of Net Services Sales</div>
                <div className={styles.right}>% of All Net Sales</div>
            </div>

            {loading ? (
                <div className={styles.loadingRow}>
                    <Spinner className={styles.spinner} />
                </div>
            ) : (
                rows.map((r, idx) => (
                    <div
                        key={(r.serviceId ?? r.serviceName ?? 'row') + idx}
                        className={styles.serviceRow}
                    >
                        <div>{r.serviceName ?? '—'}</div>
                        <div>{r.category ?? '—'}</div>

                        <div className={styles.right}>{r.quantity ?? 0}</div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.avgPrice ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.grossSales ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.discounts ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.refundColumn}>
                            <PriceFormat price={Number(r.refunds ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.netSales ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>{(Number(r.netServicesSalesPct ?? 0) * 100).toFixed(2)}%</div>
                        <div className={styles.right}>{(Number(r.allNetSalesPct ?? 0) * 100).toFixed(2)}%</div>
                    </div>
                ))
            )}

            {!loading && rows.length > 0 && (
                <div className={`${styles.serviceRow} ${styles.totalRow}`}>
                    <div>
                        Period total
                        <span className={styles.totalHint}>Includes all pages</span>
                    </div>
                    <div />
                    <div className={styles.right}>{totals.quantity}</div>
                    <div className={styles.right}>
                        <PriceFormat price={(totals.quantity > 0 ? totals.netSales / totals.quantity : 0).toFixed(2)} />
                    </div>
                    <div className={styles.right}>
                        <PriceFormat price={totals.grossSales.toFixed(2)} />
                    </div>
                    <div className={styles.right}>
                        <PriceFormat price={totals.discounts.toFixed(2)} />
                    </div>
                    <div className={styles.refundColumn}>
                        <PriceFormat price={totals.refunds.toFixed(2)} />
                    </div>
                    <div className={styles.right}>
                        <PriceFormat price={totals.netSales.toFixed(2)} />
                    </div>
                    <div className={styles.right}>{totals.netSales > 0 ? '100.00' : '0.00'}%</div>
                    <div className={styles.right}>
                        {(totals.allNetSales > 0 ? (totals.netSales / totals.allNetSales) * 100 : 0).toFixed(2)}%
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServicesTable;
